import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFormatCurrency } from "@/context/CountryContext";
import { Car, Phone, MapPin, Star, ArrowRight, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const TYPE_LABEL = { taxi: "Taxi", bus: "Bus", location: "Location", mototaxi: "Moto-taxi", navette: "Navette" };
const TYPE_COLOR = { taxi: "bg-amber-100 text-amber-700", bus: "bg-blue-100 text-blue-700", location: "bg-purple-100 text-purple-700", mototaxi: "bg-green-100 text-green-700", navette: "bg-orange-100 text-orange-700" };

export default function Transport() {
  const fmt = useFormatCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tarifs, setTarifs] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    api.get("/transport").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const openItem = async (t) => {
    setSelected(t);
    const res = await api.get(`/transport/${t.transport_id}`);
    setTarifs(res.data.tarifs || []);
  };

  const types = ["all", ...new Set(items.map((i) => i.type).filter(Boolean))];
  const filtered = typeFilter === "all" ? items : items.filter((i) => i.type === typeFilter);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Chargement…</div>;

  if (selected) return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button onClick={() => setSelected(null)} className="text-sm text-primary mb-4 hover:underline">← Retour</button>
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Badge className={`mb-2 ${TYPE_COLOR[selected.type] || "bg-muted"}`}>{TYPE_LABEL[selected.type] || selected.type}</Badge>
            <h1 className="font-display text-2xl font-bold text-secondary">{selected.nom}</h1>
            <p className="text-muted-foreground mt-1">{selected.description}</p>
          </div>
          {selected.rating > 0 && <div className="flex items-center gap-1 text-amber-500 font-semibold"><Star className="w-4 h-4 fill-amber-400" />{selected.rating}</div>}
        </div>
        <div className="mt-4 space-y-2 text-sm text-muted-foreground">
          {selected.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4" />{selected.phone}</div>}
          {selected.ville && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{selected.ville}</div>}
          {selected.zones?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {selected.zones.map((z) => <span key={z} className="bg-muted text-xs px-2 py-0.5 rounded-full">{z}</span>)}
            </div>
          )}
        </div>
        {selected.tarif_base && (
          <div className="mt-4 p-3 bg-primary/5 rounded-xl text-sm">
            <span className="text-muted-foreground">Tarif de base : </span>
            <span className="font-bold text-primary">{fmt(selected.tarif_base)}</span>
            {selected.tarif_km && <span className="text-muted-foreground ml-2">· {fmt(selected.tarif_km)}/km</span>}
          </div>
        )}
      </div>

      {tarifs.length > 0 && (
        <div className="mt-6">
          <h2 className="font-display text-lg font-bold text-secondary mb-3">Tarifs par trajet</h2>
          <div className="space-y-2">
            {tarifs.map((t) => (
              <div key={t.tarif_id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-secondary font-medium">
                  {t.trajet.split("→").map((part, i, arr) => (
                    <React.Fragment key={i}>
                      <span>{part.trim()}</span>
                      {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-primary shrink-0" />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex items-center gap-4 shrink-0 ml-4">
                  {t.duree_min && <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />~{t.duree_min} min</div>}
                  <div className="font-display font-bold text-primary">{fmt(t.prix)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary flex items-center gap-2">
            <Car className="w-7 h-7 text-primary" /> Transport
          </h1>
          <p className="text-muted-foreground mt-1">Taxi, bus, moto et navettes — comparez les tarifs à Abidjan.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {types.map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${typeFilter === t ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {t === "all" ? "Tous" : TYPE_LABEL[t] || t}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">Aucun prestataire de transport trouvé.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <div key={t.transport_id} onClick={() => openItem(t)}
              className="bg-card border border-border rounded-2xl p-5 cursor-pointer hover:shadow-md hover:border-primary/30 transition-all">
              <div className="flex items-start justify-between gap-2 mb-3">
                <Badge className={`text-xs ${TYPE_COLOR[t.type] || "bg-muted"}`}>{TYPE_LABEL[t.type] || t.type}</Badge>
                {t.rating > 0 && <div className="flex items-center gap-0.5 text-amber-500 text-xs font-semibold"><Star className="w-3.5 h-3.5 fill-amber-400" />{t.rating}</div>}
              </div>
              <h3 className="font-display font-semibold text-secondary">{t.nom}</h3>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{t.ville}</div>
                {t.tarif_base && (
                  <div className="font-display font-bold text-primary text-sm">
                    Dès {fmt(t.tarif_base)}
                  </div>
                )}
              </div>
              {t.zones?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {t.zones.slice(0, 3).map((z) => <span key={z} className="text-xs bg-muted px-1.5 py-0.5 rounded">{z}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
